/**
 * Items: things held in the hotbar that are used rather than placed as a
 * block. Item ids live above the block id range, so a hotbar slot is one
 * number either way.
 */

import { BLOCKS } from './blocks';
import { ALL_ITEM_IDS, I } from './game/itemIds';
import { itemDef, spriteOf } from './game/itemdb';
import { isPotionItem, potionName, potionOf } from './game/brewing/potions';

export const ITEM_BASE = 4096;
/** Empty bucket: right click scoops up a water source. */
export const BUCKET_ID = ITEM_BASE;
/** Water bucket: right click pours a water source. */
export const WATER_BUCKET_ID = ITEM_BASE + 1;
/** Loot items: placeable in the world but rendered with a 16x16 item icon. */
export const IRON_INGOT_ID = ITEM_BASE + 2;
export const GOLD_INGOT_ID = ITEM_BASE + 3;
export const COAL_ID_ITEM = ITEM_BASE + 4;
export const BREAD_ID_ITEM = ITEM_BASE + 5;
export const STICK_ID_ITEM = ITEM_BASE + 6;
export const SADDLE_ID = ITEM_BASE + 7;
/** Flint and steel: right-click on a block top ignites fire there. */
export const FLINT_AND_STEEL_ID = ITEM_BASE + 8;
/** Flint: a crafting ingredient. */
export const FLINT_ID = ITEM_BASE + 9;
/** Iron nugget: small crafting drop. */
export const IRON_NUGGET_ID = ITEM_BASE + 10;

/** Every registered item (creative picker order): the original eleven first, then the rest. */
export const ITEM_IDS: number[] = [
  WATER_BUCKET_ID, BUCKET_ID, IRON_INGOT_ID, GOLD_INGOT_ID, COAL_ID_ITEM, BREAD_ID_ITEM,
  STICK_ID_ITEM, SADDLE_ID, FLINT_AND_STEEL_ID, FLINT_ID, IRON_NUGGET_ID,
];
for (const id of ALL_ITEM_IDS) if (!ITEM_IDS.includes(id)) ITEM_IDS.push(id);

/** Item id -> vanilla item sprite basename (item/<name>.png in the assets dump). */
const ITEM_SPRITES: Record<number, string> = {};
for (const id of ALL_ITEM_IDS) ITEM_SPRITES[id] = spriteOf(id);

/** Off-thread-friendly lookup: every item id also has a sprite path. */
export const ICON_CYCLE: Record<number, string> = ITEM_SPRITES;

export function isItem(id: number): boolean {
  return id >= ITEM_BASE;
}

/** Display name of a hotbar entry (block or item). */
export function nameOf(id: number): string {
  return (isItem(id) ? itemDef(id).name : BLOCKS[id]?.name) ?? '';
}

/** Display name of a stack: an anvil rename (Stack.name) wins over the item's own name. */
export function stackName(s: { id: number; dmg?: number; name?: string }): string {
  if (s.name) return s.name;
  return isPotionItem(s.id) ? potionName(s.id, s.dmg ?? 0) : nameOf(s.id);
}

/** Missing-texture marker (vanilla magenta/black checker) so an absent sprite is obvious. */
function paintUnknown(ctx: CanvasRenderingContext2D, s: number): void {
  for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) {
    ctx.fillStyle = (x + y) & 1 ? '#000000' : '#f800f8';
    ctx.fillRect(x * 8 * s, y * 8 * s, 8 * s, 8 * s);
  }
}

/** Official vanilla item sprites from public/mc/ (item/<name>.png), loaded
 *  once before the HUD is built; a missing file draws the magenta/black marker. */
const itemImages = new Map<number, HTMLImageElement>();
/** Leather armor: the grayscale sprite multiplied by the dye colour, then the
 *  `_overlay` sprite on top (untinted), cached per id as a 16x16 canvas. */
const tintedIcons = new Map<number, HTMLCanvasElement>();
const overlayImages = new Map<number, HTMLImageElement>();
/** Vanilla default leather colour (#A06540). */
export const LEATHER_DYE = '#a06540';
const LEATHER_NAMES = new Set(['leather_helmet', 'leather_chestplate', 'leather_leggings', 'leather_boots']);

/** The shield has no item sprite in modern versions (vanilla renders its entity
 *  model); draw the plate's front face from the model sheet into a 16x16 icon. */
let shieldIcon: HTMLCanvasElement | null = null;
async function loadShieldIcon(): Promise<void> {
  const sheet = await loadImage('mc/entity/shield/shield_base_nopattern.png');
  if (!sheet || typeof document === 'undefined') return;
  const c = document.createElement('canvas');
  c.width = c.height = 16;
  const g = c.getContext('2d');
  if (!g) return;
  g.imageSmoothingEnabled = false;
  // plate front: 12x22 at (1,1) on the 64x64 sheet, drawn 8x15 centred
  g.drawImage(sheet, 1, 1, 12, 22, 4, 0, 8, 15);
  // the grip pokes out of the bottom edge like the vanilla icon's handle
  g.drawImage(sheet, 32, 6, 2, 6, 7, 11, 2, 4);
  shieldIcon = c;
}

async function loadImage(path: string): Promise<HTMLImageElement | null> {
  const img = new Image();
  img.src = path;
  try { await img.decode(); return img; } catch { return null; }
}

export async function loadItemIcons(): Promise<void> {
  await Promise.all(Object.entries(ITEM_SPRITES).map(async ([id, name]) => {
    const img = await loadImage(`mc/item/${name}.png`);
    if (img) itemImages.set(Number(id), img);
    if (img && LEATHER_NAMES.has(name)) {
      const ov = await loadImage(`mc/item/${name}_overlay.png`);
      if (ov) overlayImages.set(Number(id), ov);
    }
  }));
  await loadShieldIcon();
  potionOverlay = await loadImage('mc/item/potion_overlay.png');
  tintedIcons.clear();
  potionIcons.clear();
}

function tintedLeather(id: number, img: HTMLImageElement): HTMLCanvasElement | null {
  let c = tintedIcons.get(id);
  if (c) return c;
  if (typeof document === 'undefined') return null;
  c = document.createElement('canvas');
  c.width = c.height = 16;
  const g = c.getContext('2d');
  if (!g) return null;
  g.imageSmoothingEnabled = false;
  g.drawImage(img, 0, 0, 16, 16);
  g.globalCompositeOperation = 'multiply';
  g.fillStyle = LEATHER_DYE;
  g.fillRect(0, 0, 16, 16);
  // multiply filled the transparent corners too: restore the sprite's alpha
  g.globalCompositeOperation = 'destination-in';
  g.drawImage(img, 0, 0, 16, 16);
  g.globalCompositeOperation = 'source-over';
  const ov = overlayImages.get(id);
  if (ov) g.drawImage(ov, 0, 0, 16, 16);
  tintedIcons.set(id, c);
  return c;
}

/** Potion bottles: the glass sprite with the liquid overlay tinted by the potion colour (cached per id + type). */
let potionOverlay: HTMLImageElement | null = null;
const potionIcons = new Map<number, HTMLCanvasElement>();
export function potionIconFor(id: number, dmg: number): HTMLCanvasElement | null {
  const key = id * 1024 + dmg;
  let c = potionIcons.get(key);
  const img = itemImages.get(id);
  if (c || !img || typeof document === 'undefined') return c ?? null;
  c = document.createElement('canvas');
  c.width = c.height = 16;
  const g = c.getContext('2d');
  if (!g) return null;
  g.imageSmoothingEnabled = false;
  g.drawImage(img, 0, 0, 16, 16);
  if (potionOverlay) {
    const t = document.createElement('canvas');
    t.width = t.height = 16;
    const tg = t.getContext('2d')!;
    tg.drawImage(potionOverlay, 0, 0, 16, 16);
    tg.globalCompositeOperation = 'source-in';
    tg.fillStyle = '#' + potionOf(dmg).color.toString(16).padStart(6, '0');
    tg.fillRect(0, 0, 16, 16);
    g.drawImage(t, 0, 0);
  }
  potionIcons.set(key, c);
  return c;
}

/** Paint an item's icon over the whole of a square canvas `size` px wide. */
export function drawItemIcon(ctx: CanvasRenderingContext2D, id: number, size: number): void {
  if (id === I.SHIELD && shieldIcon) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(shieldIcon, 0, 0, size, size);
    return;
  }
  const img = itemImages.get(id);
  if (img) {
    ctx.imageSmoothingEnabled = false;
    const leather = LEATHER_NAMES.has(ITEM_SPRITES[id]) ? tintedLeather(id, img) : null;
    ctx.drawImage(leather ?? img, 0, 0, size, size);
    return;
  }
  paintUnknown(ctx, size / 16);
}
